import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, collection, addDoc, serverTimestamp, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../services/firebase";

import { useUserData } from "../hooks/useUserData";
import NavbarMobile from "../components/NavbarMobile";
import BottomNavTechnician from "../components/technician/BottomNavTechnician";
import { Wrench, Clock, CheckCircle, Camera, FileText, MapPin, User, Calendar } from "lucide-react";
import { addLog } from "../utils/audit";

export default function TechnicianTaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [user, userLoading] = useUserData();
  const [task, setTask] = useState(null);
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [laborHours, setLaborHours] = useState("");
  const [partsUsed, setPartsUsed] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (userLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    // Load task details
    loadTaskDetails(user.uid);

    // Set up photos listener
    const photosQuery = query(
      collection(db, "maintenancePhotos"),
      where("taskId", "==", taskId)
    );

    const unsubPhotos = onSnapshot(photosQuery, (snap) => {
      const photosData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPhotos(photosData);
    });

    return () => unsubPhotos();
  }, [user, userLoading, taskId, navigate]);

  const loadTaskDetails = async (userId) => {
    try {
      const taskDoc = await getDoc(doc(db, "maintenanceTasks", taskId));
      if (!taskDoc.exists()) {
        console.error("Task not found");
        navigate("/technician/tasks");
        return;
      }

      const taskData = { id: taskDoc.id, ...taskDoc.data() };
      setTask(taskData);

      // Load asset details
      if (taskData.assetId) {
        const assetDoc = await getDoc(doc(db, "assets", taskData.assetId));
        if (assetDoc.exists()) {
          setAsset({ id: assetDoc.id, ...assetDoc.data() });
        }
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading task details:", error);
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      // Update task status
      await updateDoc(doc(db, "maintenanceTasks", taskId), {
        status: newStatus,
        lastUpdated: new Date(),
        updatedBy: user.uid
      });

      // Update asset status when task becomes in-progress
      if (newStatus === "in-progress") {
        await updateDoc(doc(db, "assets", task.assetId), {
          status: "under maintenance",
          lastUpdated: new Date(),
        });
      }

      // Log the action
      await addLog(user.uid, "MAINTENANCE_STATUS_UPDATE", {
        taskId,
        assetId: task.assetId,
        oldStatus: task.status,
        newStatus,
        assetName: asset?.name
      });

      // Update local task state
      setTask(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };




  const compressImage = (file, maxWidth = 800, quality = 0.6) => {
    return new Promise((resolve, reject) => {
      // Check file size first - if already small, skip compression
      if (file.size < 500000) { // Less than 500KB
        resolve(file);
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions - more aggressive compression
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          // Further reduce if still large
          if (width > 600 && height > 600) {
            const ratio = Math.min(600 / width, 600 / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress with lower quality
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(resolve, 'image/jpeg', quality);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size too large. Please select an image smaller than 10MB.');
      return;
    }

    setUploadingPhoto(true);
    try {
      console.log(`Original file size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

      // Compress the image before uploading
      const compressedBlob = await compressImage(file);
      console.log(`Compressed file size: ${(compressedBlob.size / 1024 / 1024).toFixed(2)}MB`);

      const compressedFile = new File([compressedBlob], file.name, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Upload to Cloudinary (same as Profile.jsx)
      const formData = new FormData();
      formData.append("file", compressedBlob);
      formData.append("upload_preset", "profilePictures");

      const res = await fetch(
        "https://api.cloudinary.com/v1_1/dse3b23i2/image/upload",
        { method: "POST", body: formData }
      );

      if (!res.ok) {
        const errorData = await res.text();
        console.error("Cloudinary upload failed:", res.status, errorData);
        throw new Error(`Upload failed: ${res.status} ${errorData}`);
      }

      const data = await res.json();
      console.log("Cloudinary response:", data);

      if (!data.secure_url) {
        console.error("No secure_url in response:", data);
        throw new Error("Upload succeeded but no URL returned");
      }

      const downloadURL = data.secure_url;

      // Save photo metadata to Firestore
      await addDoc(collection(db, "maintenancePhotos"), {
        taskId,
        assetId: task.assetId,
        technicianId: user.uid,
        technicianName: user.name,
        photoURL: downloadURL,
        fileName: file.name,
        originalSize: file.size,
        compressedSize: compressedBlob.size,
        uploadedAt: serverTimestamp()
      });

      // Log the action
      await addLog(user.uid, "MAINTENANCE_PHOTO_UPLOADED", {
        taskId,
        assetId: task.assetId,
        assetName: asset?.name,
        fileName: file.name,
        originalSize: file.size,
        compressedSize: compressedBlob.size
      });

      console.log('Photo uploaded successfully');

    } catch (error) {
      console.error("Error uploading photo:", error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleCompleteTask = async () => {
    try {
      const technicianNotes = notes.trim();
      const completionData = {
        status: "awaiting-confirmation", // Changed from "completed" to "awaiting-confirmation"
        completedAt: serverTimestamp(),
        completedBy: user.uid,
        laborHours: laborHours ? parseFloat(laborHours) : null,
        partsUsed: partsUsed.trim() || null,
        technicianNotes: technicianNotes || null, // Add technician notes for admin visibility
        lastUpdated: serverTimestamp()
      };

      // Update maintenance task status
      await updateDoc(doc(db, "maintenanceTasks", taskId), completionData);

      // Also update the corresponding request status and add completion details for admin visibility
      if (task.requestId) {
        const requestUpdateData = {
          status: "awaiting-confirmation",
          technicianNotes: technicianNotes, // Add technician notes to request so admin can see them
          laborHours: completionData.laborHours,
          partsUsed: completionData.partsUsed,
          completedAt: completionData.completedAt,
          completedBy: completionData.completedBy,
          lastUpdated: serverTimestamp()
        };
        await updateDoc(doc(db, "requests", task.requestId), requestUpdateData);
      }

      // Log the completion
      await addLog(user.uid, "MAINTENANCE_TASK_COMPLETED", {
        taskId,
        assetId: task.assetId,
        assetName: asset?.name,
        laborHours: completionData.laborHours,
        partsUsed: completionData.partsUsed,
        technicianNotes: technicianNotes,
        status: "awaiting-confirmation",
        requestId: task.requestId
      });

      alert("✅ Task completed! Waiting for admin confirmation.");
      navigate("/technician/tasks");
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const canUpdateStatus = (currentStatus, newStatus) => {
    const validTransitions = {
      "pending": ["in-progress", "cancelled"],
      "in-progress": ["awaiting-confirmation", "awaiting-parts", "pending"],
      "awaiting-parts": ["in-progress", "cancelled"],
      "awaiting-confirmation": [],
      "completed": [],
      "cancelled": []
    };
    return validTransitions[currentStatus]?.includes(newStatus) || false;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending": return "text-warning-600 bg-warning-50";
      case "in-progress": return "text-info-600 bg-info-50";
      case "completed": return "text-success-600 bg-success-50";
      case "awaiting-parts": return "text-neutral-600 bg-neutral-50";
      case "awaiting-confirmation": return "text-purple-600 bg-purple-50";
      case "cancelled": return "text-error-600 bg-error-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high": return "text-error-600 bg-error-50";
      case "medium": return "text-warning-600 bg-warning-50";
      case "low": return "text-info-600 bg-info-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <div className="skeleton-avatar mb-4"></div>
        <div className="skeleton-title w-48 mb-2"></div>
        <div className="skeleton-text w-32"></div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col justify-center items-center animate-fade-in">
        <Wrench className="w-16 h-16 text-neutral-400 mb-4" />
        <h1 className="text-xl font-semibold text-neutral-900 mb-2">Task Not Found</h1>
        <p className="text-neutral-600 mb-6">The maintenance task you're looking for doesn't exist.</p>
        <button onClick={() => navigate("/technician/tasks")} className="btn-primary">
          Back to Tasks
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 animate-fade-in">
      <div className="container-fluid pt-6 space-y-6">
        {/* Task Header */}
        <div className="card">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-xl font-semibold text-neutral-900">{task.description}</h1>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                  {task.priority} Priority
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(task.status)}`}>
                  {task.status}
                </span>
              </div>
            </div>
          </div>

          {/* Asset Information */}
          {asset && (
            <div className="bg-neutral-50 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-neutral-900 mb-2">Asset Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-neutral-500" />
                  <span className="font-medium">{asset.name || "Unnamed Asset"}</span>
                </div>
                {asset.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-neutral-500" />
                    <span>{asset.location}</span>
                  </div>
                )}
                {asset.category && (
                  <div className="flex items-center gap-2">
                    <span>Category: {asset.category}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Task Details */}
          <div className="space-y-3 text-sm text-neutral-600">
            {task.dueDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Due: {new Date(task.dueDate.toDate()).toLocaleDateString()}</span>
              </div>
            )}
            {task.requesterName && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Requested by: {task.requesterName}</span>
              </div>
            )}
            {task.assignedBy && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Assigned by: {task.assignedBy}</span>
              </div>
            )}
            {task.createdAt && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Created: {new Date(task.createdAt.toDate()).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Update Actions */}
        <div className="card">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Update Status</h3>
          <div className="grid grid-cols-2 gap-3">
            {task.status === "pending" && (
              <button
                onClick={() => handleStatusUpdate("in-progress")}
                className="p-3 rounded-lg text-sm font-medium transition-all bg-info-100 text-info-700 hover:bg-info-200"
              >
                Start Work
              </button>
            )}
            {task.status === "in-progress" && (
              <div className="p-3 rounded-lg text-sm font-medium bg-success-100 text-success-700">
                Work in Progress
              </div>
            )}
            {(task.status === "awaiting-parts" || task.status === "awaiting-confirmation" || task.status === "completed") && (
              <div className="p-3 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-700">
                {task.status === "awaiting-parts" ? "Awaiting Parts" :
                 task.status === "awaiting-confirmation" ? "Awaiting Confirmation" :
                 task.status === "completed" ? "Completed" : task.status}
              </div>
            )}
          </div>
        </div>

        {/* Photo Upload */}
        <div className="card">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Photos
          </h3>

          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploadingPhoto}
              className="hidden"
              id="photo-upload"
            />
            <label
              htmlFor="photo-upload"
              className={`btn-secondary w-full flex items-center justify-center gap-2 ${
                uploadingPhoto ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <Camera className="w-5 h-5" />
              {uploadingPhoto ? "Uploading..." : "Take/Add Photo"}
            </label>

            {/* Photo Gallery */}
            {photos.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {photos.map((photo) => (
                  <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-neutral-100">
                    <img
                      src={photo.photoURL}
                      alt={photo.fileName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Add Notes */}
        <div className="card">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Notes
          </h3>

          <div className="space-y-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add maintenance notes, observations, or findings... (will be sent to admin when completing task)"
              className="w-full p-3 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={4}
            />
            <p className="text-xs text-neutral-500">
              Notes will be sent to the admin when you complete the task
            </p>
          </div>
        </div>

        {/* Completion Form */}
        {task.status === "in-progress" && (
          <div className="card">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Complete Task</h3>

            <div className="space-y-4">
              <div>
                <label className="form-label">Labor Hours</label>
                <input
                  type="number"
                  step="0.5"
                  value={laborHours}
                  onChange={(e) => setLaborHours(e.target.value)}
                  placeholder="e.g. 2.5"
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">Parts Used</label>
                <textarea
                  value={partsUsed}
                  onChange={(e) => setPartsUsed(e.target.value)}
                  placeholder="List any parts used during maintenance..."
                  className="form-input resize-none"
                  rows={3}
                />
              </div>

              <button
                onClick={handleCompleteTask}
                className="btn-success w-full py-3 flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Complete Task
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
